import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { AlertCircleFill, ArrowRightOutline, Close, PlayOutline, PlusOutline } from '@lyearn/icons';
import { Button, Stack, Switch, Typography } from '@lyearn/ui';
import { Modal } from '@/components/Modals';
import { ModalVariant } from '@/components/Modals/types';
import { VideoCharacterProviderEnum } from '@/types/schema';

import {
  SlideEditor,
  SlideEditorSkeleton,
  SlideListItem,
  SlideListItemSkeleton,
  type StoryboardSlide,
  VideoSettingsPopover,
  type VideoSlideTemplate,
  VideoTemplatePicker,
} from './components';
import GenerateVideoBanner from './GenerateVideoBanner';
import { useVideoCharacterAvatars } from './useVideoCharacterAvatars';

interface StoryboardModalProps {
  slides: StoryboardSlide[];
  selectedSlideIndex: number;
  switchingSlideIndex?: number | null;
  onClose: () => void;
  onGenerateVideo: () => void;
  onAddSlide: (slideTemplateId: string, template: VideoSlideTemplate) => void;
  onDeleteSlide: (slideIndex: number) => void;
  onChangeSlideTemplate: (
    slideIndex: number,
    slideTemplateId: string,
    template: VideoSlideTemplate,
  ) => void;
  onSelectSlide: (index: number) => void;
  onUpdateTranscript: (slideIndex: number, transcript: string) => void;
  onUpdateVariable: (slideIndex: number, key: string, value: string) => void;
  isGeneratingVideo?: boolean;
  isGenerationDisabled?: boolean;
  isLoading?: boolean;
  avatarId?: string;
  voiceId?: string;
  onApplySettings?: (avatarId: string, voiceId: string) => void;
  isGeneratingScript?: boolean;
  onEditScriptAndStyle?: () => void;
}

const StoryboardModal = (props: StoryboardModalProps) => {
  const {
    slides,
    selectedSlideIndex,
    switchingSlideIndex,
    onClose,
    onGenerateVideo,
    isGeneratingVideo,
    isGenerationDisabled,
    onAddSlide,
    onDeleteSlide,
    onChangeSlideTemplate,
    isLoading,
    onSelectSlide,
    onUpdateTranscript,
    onUpdateVariable,
    avatarId = '',
    voiceId = '',
    onApplySettings,
    isGeneratingScript,
    onEditScriptAndStyle,
  } = props;
  const { t } = useTranslation('Course');
  const { avatars } = useVideoCharacterAvatars({
    filters: { providers: [VideoCharacterProviderEnum.Heygen] },
  });
  const storyboardDomId = useId().replace(/:/g, '');
  const showScriptStatusBanner = Boolean(onEditScriptAndStyle && isGeneratingScript);
  const isDocumentImageMode = slides.some((slide) => Boolean(slide.variables?.image?.trim()));
  const selectedAvatarPreviewUrl =
    avatars.find((avatar) => avatar.avatar_id === avatarId)?.preview_image_url ||
    avatars[0]?.preview_image_url ||
    '';
  const [showAvatarPosition, setShowAvatarPosition] = useState(true);
  const [showAvatarPositionHint, setShowAvatarPositionHint] = useState(true);

  const getAvatarOverlayPosition = useCallback(
    (previewUrl?: string): 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' => {
      const decoded = decodeURIComponent(previewUrl || '').toLowerCase();
      if (
        decoded.includes('top_left') ||
        decoded.includes('top-left') ||
        decoded.includes('top left')
      ) {
        return 'top-left';
      }
      if (
        decoded.includes('top_right') ||
        decoded.includes('top-right') ||
        decoded.includes('top right')
      ) {
        return 'top-right';
      }
      if (
        decoded.includes('bottom_left') ||
        decoded.includes('bottom-left') ||
        decoded.includes('bottom left')
      ) {
        return 'bottom-left';
      }
      return 'bottom-right';
    },
    [],
  );

  const getSlideListItemId = useCallback(
    (slideIndex: number) => `${storyboardDomId}-slide-list-item-${slideIndex}`,
    [storyboardDomId],
  );

  const getSlideEditorId = useCallback(
    (slideIndex: number) => `${storyboardDomId}-slide-editor-${slideIndex}`,
    [storyboardDomId],
  );

  const videoSettingsButton = useMemo(
    () =>
      onApplySettings ? (
        <VideoSettingsPopover
          avatarId={avatarId}
          voiceId={voiceId}
          onApplySettings={(config: {
            avatarId: string;
            voiceId: string;
            videoType: 'avatarNarration' | 'voiceOver';
          }) => onApplySettings(config.avatarId, config.voiceId)}
        />
      ) : null,
    [avatarId, onApplySettings, voiceId],
  );

  const renderLoadingHeader = useCallback(
    () => (
      <Stack
        alignItems="center"
        className="sticky top-0 z-50 border-b border-border bg-bg-primary pl-24 pr-16 py-16"
        direction="row"
        justifyContent="between">
        <Typography color="text-text-primary" variant="heading-s">
          Storyboard
        </Typography>
        <Stack alignItems="center" direction="row" gap={8}>
          {videoSettingsButton}
          <Stack
            alignItems="center"
            aria-label={t('Common:Actions.CloseDialog')}
            className="cursor-pointer rounded-[1.6rem] bg-bg-primary p-12"
            justifyContent="center"
            onClick={onClose}>
            <Close className="h-24 w-24 text-text-primary" />
          </Stack>
        </Stack>
      </Stack>
    ),
    [onClose, t, videoSettingsButton],
  );

  const skipEditorScrollRef = useRef(false);
  const [slideListScrollContainerEl, setSlideListScrollContainerEl] =
    useState<HTMLDivElement | null>(null);
  const [slideEditorScrollContainerEl, setSlideEditorScrollContainerEl] =
    useState<HTMLDivElement | null>(null);
  const isProgrammaticScrollRef = useRef(false);
  const lockedSelectionIndexRef = useRef<number | null>(null);
  const programmaticScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingScrollIndexRef = useRef<number | null>(null);

  const onSelectSlideRef = useRef(onSelectSlide);
  onSelectSlideRef.current = onSelectSlide;
  const slidesRef = useRef(slides);
  slidesRef.current = slides;
  const selectedSlideIndexRef = useRef(selectedSlideIndex);
  selectedSlideIndexRef.current = selectedSlideIndex;

  const scrollContainerToNode = useCallback(
    (container: HTMLDivElement | null, nodeId: string, block: 'start' | 'nearest') => {
      if (!container) {
        return;
      }

      const node = document.getElementById(nodeId);
      if (!node) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const nodeRect = node.getBoundingClientRect();
      let nextScrollTop = container.scrollTop;

      if (block === 'start') {
        nextScrollTop += nodeRect.top - containerRect.top;
      } else if (nodeRect.top < containerRect.top) {
        nextScrollTop -= containerRect.top - nodeRect.top + 12;
      } else if (nodeRect.bottom > containerRect.bottom) {
        nextScrollTop += nodeRect.bottom - containerRect.bottom + 12;
      } else {
        return;
      }

      container.scrollTo({
        behavior: 'smooth',
        top: Math.max(nextScrollTop, 0),
      });
    },
    [],
  );

  useEffect(() => {
    const container = slideEditorScrollContainerEl;
    if (!container || slides.length === 0) return;

    lockedSelectionIndexRef.current = null;
    isProgrammaticScrollRef.current = false;
    skipEditorScrollRef.current = false;
    pendingScrollIndexRef.current = null;
    if (programmaticScrollTimerRef.current) {
      clearTimeout(programmaticScrollTimerRef.current);
      programmaticScrollTimerRef.current = null;
    }

    let frameId: number | null = null;

    const syncSelectedSlideFromScroll = () => {
      if (isProgrammaticScrollRef.current) return;
      if (lockedSelectionIndexRef.current !== null) return;

      const slideNodes = Array.from(container.querySelectorAll<HTMLElement>('[data-slide-index]'));
      if (slideNodes.length === 0) return;

      const containerRect = container.getBoundingClientRect();
      const activationLine = containerRect.top + Math.min(120, containerRect.height * 0.25);
      let nextSelectedIndex = Number(slideNodes[0].getAttribute('data-slide-index'));

      slideNodes.forEach((node) => {
        const nodeIndex = Number(node.getAttribute('data-slide-index'));
        if (node.getBoundingClientRect().top <= activationLine) {
          nextSelectedIndex = nodeIndex;
        }
      });

      if (nextSelectedIndex === selectedSlideIndexRef.current) return;

      skipEditorScrollRef.current = true;
      onSelectSlideRef.current(nextSelectedIndex);
      requestAnimationFrame(() => {
        skipEditorScrollRef.current = false;
      });
    };

    const requestSyncSelectedSlideFromScroll = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        syncSelectedSlideFromScroll();
      });
    };

    requestSyncSelectedSlideFromScroll();
    container.addEventListener('scroll', requestSyncSelectedSlideFromScroll, { passive: true });

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      container.removeEventListener('scroll', requestSyncSelectedSlideFromScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideEditorScrollContainerEl, slides.length]);

  useEffect(() => {
    const selectedSlide = slidesRef.current[selectedSlideIndex];

    if (!selectedSlide) {
      pendingScrollIndexRef.current = selectedSlideIndex;
      isProgrammaticScrollRef.current = true;
      return;
    }

    const isPendingNewSlideScroll = pendingScrollIndexRef.current === selectedSlideIndex;

    if (isPendingNewSlideScroll) {
      pendingScrollIndexRef.current = null;
    }

    scrollContainerToNode(
      slideListScrollContainerEl,
      getSlideListItemId(selectedSlideIndex),
      isPendingNewSlideScroll ? 'start' : 'nearest',
    );

    if (!skipEditorScrollRef.current) {
      lockedSelectionIndexRef.current = selectedSlideIndex;

      if (programmaticScrollTimerRef.current) {
        clearTimeout(programmaticScrollTimerRef.current);
      }
      isProgrammaticScrollRef.current = true;
      programmaticScrollTimerRef.current = setTimeout(
        () => {
          isProgrammaticScrollRef.current = false;
          lockedSelectionIndexRef.current = null;
          programmaticScrollTimerRef.current = null;
        },
        isPendingNewSlideScroll ? 1200 : 700,
      );

      requestAnimationFrame(() => {
        scrollContainerToNode(
          slideEditorScrollContainerEl,
          getSlideEditorId(selectedSlideIndex),
          'start',
        );
      });
    }
  }, [
    getSlideEditorId,
    getSlideListItemId,
    scrollContainerToNode,
    selectedSlideIndex,
    slides.length,
    slideEditorScrollContainerEl,
    slideListScrollContainerEl,
  ]);

  useEffect(() => {
    return () => {
      if (programmaticScrollTimerRef.current) {
        clearTimeout(programmaticScrollTimerRef.current);
      }
    };
  }, []);

  return (
    <Modal
      className="!w-[120rem] !max-h-[80rem] !min-h-[60rem]"
      classes={{ paper: '!w-[120rem] !max-h-[80rem] !min-h-[60rem]' }}
      footer={
        <Stack
          alignItems="center"
          className="border-t border-border px-24 py-12"
          direction="row"
          gap={12}
          justifyContent="end">
          {isLoading ? (
            <Button disabled StartIcon={PlayOutline} size="medium" variant="outlined">
              {t('Course:AiVideoGeneration.Preview')}
            </Button>
          ) : null}
          <Button
            EndIcon={ArrowRightOutline}
            disabled={isGenerationDisabled || isLoading}
            loading={isGeneratingVideo}
            size="medium"
            variant="filled"
            onClick={onGenerateVideo}>
            {t('Course:AiVideoGeneration.GenerateVideo')}
          </Button>
        </Stack>
      }
      header={isLoading ? undefined : undefined}
      renderHeader={
        isLoading
          ? renderLoadingHeader
          : () => (
              <Stack
                alignItems="center"
                className="border-b border-border pl-24 pr-16 py-16"
                direction="row"
                justifyContent="between">
                <Typography color="text-text-primary" variant="heading-s">
                  {t('Course:AiVideoGeneration.CreateAIVideo')}
                </Typography>
                <Stack alignItems="center" direction="row" gap={8}>
                  {videoSettingsButton}
                  <Stack
                    alignItems="center"
                    aria-label={t('Common:Actions.CloseDialog')}
                    className="cursor-pointer rounded-[1.6rem] bg-bg-primary p-12"
                    justifyContent="center"
                    onClick={onClose}>
                    <Close className="h-24 w-24 text-text-primary" />
                  </Stack>
                </Stack>
              </Stack>
            )
      }
      showCloseIcon={false}
      variant={ModalVariant.ExtraWide}
      onClose={onClose}>
      <Stack className="h-full flex-1 shrink overflow-hidden" direction="col">
        {showScriptStatusBanner ? (
          <GenerateVideoBanner
            className="px-24 pt-20 pb-4"
            state="script_generating"
            onCreateVideoClick={onEditScriptAndStyle || (() => undefined)}
          />
        ) : null}
        <Stack className="flex-1 min-h-0 overflow-hidden" direction="row">
          <Stack
            ref={setSlideListScrollContainerEl}
            className="w-[24rem] min-w-[24rem] overflow-y-auto px-12 py-16"
            direction="col"
            gap={16}>
            {isLoading
              ? Array.from({ length: 5 }, (_, i) => <SlideListItemSkeleton key={i} index={i} />)
              : slides.map((slide, i) => (
                  <div key={i} id={getSlideListItemId(i)}>
                    <SlideListItem
                      avatarPosition={getAvatarOverlayPosition(slide.previewUrl)}
                      avatarPreviewUrl={
                        slide.variables?.image?.trim() && showAvatarPosition
                          ? selectedAvatarPreviewUrl
                          : undefined
                      }
                      index={i}
                      isSelected={i === selectedSlideIndex}
                      previewUrl={slide.variables?.image?.trim() || slide.previewUrl}
                      onClick={() => onSelectSlide(i)}
                    />
                  </div>
                ))}
          </Stack>

          <Stack className="flex-1 min-w-0 min-h-0 overflow-hidden" direction="col">
            <Stack
              alignItems="center"
              className="px-24 py-12"
              direction="row"
              justifyContent="between">
              {isLoading ? (
                <>
                  <Stack alignItems="center" direction="row" gap={8}>
                    <Stack
                      aria-label={t('Course:AiVideoGeneration.GeneratingStoryboard')}
                      className="h-[2rem] w-[2rem] animate-spin rounded-full border-2 border-divider border-t-text-primary"
                    />
                    <Typography color="text-text-placeholder" variant="heading-xs">
                      {t('Course:AiVideoGeneration.GeneratingStoryboard')}
                    </Typography>
                  </Stack>
                  <Button disabled StartIcon={PlusOutline} size="small" variant="ghost">
                    Add Slide
                  </Button>
                </>
              ) : (
                <>
                  <Typography color="text-text-placeholder" variant="heading-xs">
                    {slides.length} {t('Course:AiVideoGeneration.Slides')}
                  </Typography>
                  {isDocumentImageMode ? (
                    <Stack alignItems="center" direction="row" gap={12}>
                      <Typography color="text-text-primary" variant="label-01">
                        See Avatar Position
                      </Typography>
                      <Switch
                        checked={showAvatarPosition}
                        size="small"
                        onChange={() => setShowAvatarPosition((prev) => !prev)}
                      />
                    </Stack>
                  ) : (
                    <VideoTemplatePicker
                      selectedTemplateId=""
                      onChangeTemplate={(slideTemplateId, template) =>
                        onAddSlide(slideTemplateId, template)
                      }>
                      <Button StartIcon={PlusOutline} shape="square" size="small" variant="ghost">
                        Add Slide
                      </Button>
                    </VideoTemplatePicker>
                  )}
                </>
              )}
            </Stack>
            {!isLoading && isDocumentImageMode && showAvatarPosition && showAvatarPositionHint ? (
              <Stack className="px-24 pb-24">
                <Stack
                  alignItems="center"
                  className="rounded-8 bg-bg-warning-light px-12 py-12 w-full"
                  direction="row"
                  justifyContent="between">
                  <Stack alignItems="center" className="min-w-0" direction="row" gap={12}>
                    <AlertCircleFill className="h-24 w-24 shrink-0 text-icon-warning" />
                    <Stack alignItems="center" className="min-w-0" direction="row" gap={8}>
                      <Typography
                        className="leading-[2.4rem]"
                        color="text-text-primary"
                        variant="body-short-01">
                        For best results, choose avatar position so it does not interfere with slide
                        content
                      </Typography>
                      {/* <QuestionMarkCircleOutline className="h-18 w-18 shrink-0 text-icon-secondary" /> */}
                    </Stack>
                  </Stack>
                  <Stack
                    alignItems="center"
                    aria-label={t('Common:Actions.Close')}
                    className="h-24 w-24 shrink-0 cursor-pointer"
                    justifyContent="center"
                    onClick={() => setShowAvatarPositionHint(false)}>
                    <Close className="h-24 w-24 text-icon-placeholder" />
                  </Stack>
                </Stack>
              </Stack>
            ) : null}

            <div
              ref={setSlideEditorScrollContainerEl}
              className="flex flex-col gap-16 overflow-y-auto flex-1 min-h-0 py-16">
              {isLoading
                ? Array.from({ length: 2 }, (_, i) => (
                    <SlideEditorSkeleton key={i} slideIndex={i} t={t} />
                  ))
                : slides.map((slide, i) => (
                    <div key={i} data-slide-index={i} id={getSlideEditorId(i)}>
                      <SlideEditor
                        avatarPreviewUrl={showAvatarPosition ? selectedAvatarPreviewUrl : undefined}
                        isSwitchingTemplate={switchingSlideIndex === i}
                        slide={slide}
                        slideIndex={i}
                        onChangeSlideTemplate={onChangeSlideTemplate}
                        onDeleteSlide={onDeleteSlide}
                        onUpdateTranscript={(transcript) => onUpdateTranscript(i, transcript)}
                        onUpdateVariable={(index, key, value) =>
                          onUpdateVariable(index, key, value)
                        }
                      />
                    </div>
                  ))}
            </div>
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default StoryboardModal;
